import { useState } from "react";
import { useGetTransactionsQuery } from "@/services/api/transaction/transactionApi";

/**
 * Custom hook for managing transaction data
 * Provides pagination and search functionality for transactions
 */
export const useTransactionData = () => {
  // ==========================================
  // Pagination State
  // ==========================================
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
  });

  // ==========================================
  // Search State
  // ==========================================
  const [search, setSearch] = useState("");

  // ==========================================
  // Pagination Handlers
  // ==========================================
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (per_page) => {
    setPagination({ page: 1, per_page });
  };

  // ==========================================
  // Search Handler
  // ==========================================
  const handleSearch = (searchTerm) => {
    const trimmedSearch = searchTerm.trim();
    setSearch(trimmedSearch);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const {
    data: apiResponse = {},
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetTransactionsQuery({
    page: pagination.page,
    per_page: pagination.per_page,
    search: search,
  });

  // Extract data from nested response structure
  // API returns: { data: { original: { data: [...], current_page, per_page, total } } }
  const responseData = apiResponse?.original || apiResponse;
  const transactionsData = responseData.data || [];
  const transactionSummary = {
    total_transaction: responseData.total_transaction_amount || 0,
    total_abandoned: responseData.total_abandoned || 0,
    total_failed: responseData.total_failed || 0,
    total_success: responseData.total_success || 0,
  };
  const paginationInfo = {
    current_page: responseData.current_page || 1,
    last_page: responseData.last_page || 1,
    per_page: responseData.per_page || 10,
    total: responseData.total || 0,
  };

  return {
    // Data
    transactionsData,
    apiResponse,
    transactionSummary,
    // Pagination info
    paginationInfo,

    // Pagination handlers
    handlePageChange,
    handlePageSizeChange,

    // Search state and handler
    search,
    handleSearch,

    // Loading states
    isLoading,
    isFetching,

    // Error states
    error,
    isError: !!error,

    // Actions
    refetch,
  };
};
